import { formatMoney, minorUnitsFor } from "@sylviq/ui-core";
import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { Tabs } from "@sylviq/ui-react-web/components/ui/tabs";
import { SearchSelect } from "@sylviq/ui-react-web/components/ui/search-select";
import { DataTableBlock } from "@sylviq/ui-react-web/blocks/table";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { CardField } from "@/components/RecordCard";
import { useViewSearch } from "@/lib/view-search";
import { WorklistSummary } from "@/components/WorklistSummary";
import { BillRowActions } from "./BillRowActions";
import { RecurringBillsPage } from "@/features/recurring-bills/RecurringBillsPage";
import { SupplierCreditsPage } from "@/features/supplier-credits/SupplierCreditsPage";
import { ReceiptIntake } from "./ReceiptIntake";
import {
  clearDocumentDraft,
  emptyDraftLine,
  loadDocumentDraft,
  newDocumentDraft,
  saveDocumentDraft,
  todayInput,
  type DocumentDraft,
} from "@/features/document-lines/documentDraft";
import { DocumentLineRow } from "@/features/document-lines/DocumentLineRow";
import {
  toBillLinePayload,
  toMinor,
} from "@/features/document-lines/productDetails";
import {
  PaymentTermPicker,
  usePaymentTerms,
} from "@/features/payment-terms/PaymentTermPicker";
import { dateInputToUtcIso } from "@/features/payment-terms/paymentTermDate";
import { minorUnitEntry, toMinorUnits } from "@/lib/money";
import { totalTaxableLines } from "@/lib/tax";
import { useOrganization } from "@/lib/organization";
import {
  ApiRequestError,
  type BankAccount,
  type Bill,
  type BillCounts,
  createBill,
  listBankAccounts,
  getBillCounts,
  listBills,
  listTaxRates,
  payBill,
  type TaxRate,
  transitionBill,
} from "@/lib/api";
import {
  billBalanceCents,
  billNeedsAttention,
  isPartPaid,
  matchesBillListFilter,
  recordDueLabel,
} from "@/lib/worklist";

const BILL_DRAFT_PREFIX = "niche-accounting.bill-draft.v1";

function statusTone(
  status: string,
): "success" | "warning" | "danger" | "neutral" {
  switch (status.toLowerCase()) {
    case "paid":
      return "success";
    case "overdue":
      return "danger";
    case "received":
      return "warning";
    default:
      return "neutral";
  }
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

const EMPTY_SELECTION: ReadonlySet<string> = new Set<string>();
const ignoreSelectionChange = () => {};

interface KnownSupplier {
  name: string;
  crmContactId?: string;
}

export function BillsPage() {
  const { scope } = useOrganization();
  const organizationId = scope?.organization_id ?? "default";

  const [bills, setBills] = useState<Bill[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Payables rollup (aging + per-status counts), separate from the list so a
  // failed read shows an honest error instead of a fake zero.
  const [counts, setCounts] = useState<BillCounts | null>(null);
  const [countsLoading, setCountsLoading] = useState(true);
  const [countsError, setCountsError] = useState<string | null>(null);

  // Client-side "due within 7 days" attention toggle.
  const [attentionOnly, setAttentionOnly] = useState(false);

  const { terms: paymentTerms, error: paymentTermsError } = usePaymentTerms();
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  useEffect(() => {
    let cancelled = false;
    listTaxRates()
      .then((rows) => {
        if (!cancelled) setTaxRates(rows);
      })
      .catch(() => {
        if (!cancelled) setTaxRates([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Creation draft, mirrored per organization (see documentDraft).
  const [draft, setDraft] = useState<DocumentDraft>(newDocumentDraft);
  const [loadedDraftKey, setLoadedDraftKey] = useState<string | null>(null);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Client-side table controls over the fetched list.
  const [viewTab, setViewTab] = useViewSearch<"bills" | "recurring" | "credits">(
    "view",
    ["bills", "recurring", "credits"],
    "bills",
  );
  const [statusTab, setStatusTab] = useViewSearch<
    "" | "draft" | "received" | "overdue" | "paid" | "void"
  >("status", ["draft", "received", "overdue", "paid", "void"], "");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState<"due" | "total" | "number" | "supplier">(
    "due",
  );
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Record-payment drawer.
  const [payOpen, setPayOpen] = useState(false);
  const [payBillId, setPayBillId] = useState("");
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("bank_transfer");
  const [payDate, setPayDate] = useState(todayInput);
  const [payAccountId, setPayAccountId] = useState("");
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [paying, setPaying] = useState(false);
  const [payDone, setPayDone] = useState<string | null>(null);

  const [acting, setActing] = useState<string | null>(null);
  const [confirmRecognize, setConfirmRecognize] = useState<Bill | null>(null);

  const loadCounts = useCallback(() => {
    setCountsLoading(true);
    setCountsError(null);
    getBillCounts()
      .then(setCounts)
      .catch((cause: unknown) => setCountsError(toErrorMessage(cause)))
      .finally(() => setCountsLoading(false));
  }, []);

  useEffect(() => {
    listBankAccounts()
      .then(setBankAccounts)
      .catch(() => setBankAccounts([]));
  }, []);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    listBills()
      .then(setBills)
      .catch((cause: unknown) => setError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
    loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    setDraft(loadDocumentDraft(BILL_DRAFT_PREFIX, organizationId) ?? newDocumentDraft());
    setLoadedDraftKey(organizationId);
  }, [organizationId]);
  useEffect(() => {
    if (loadedDraftKey !== organizationId) return;
    saveDocumentDraft(BILL_DRAFT_PREFIX, organizationId, draft);
  }, [draft, loadedDraftKey, organizationId]);

  const updateDraft = (patch: Partial<DocumentDraft>) =>
    setDraft((prev) => ({ ...prev, ...patch }));

  const updateLine = (index: number, patch: Partial<typeof draft.lines[number]>) => {
    setDraft((prev) => ({
      ...prev,
      lines: prev.lines.map((line, i) =>
        i === index ? { ...line, ...patch } : line,
      ),
    }));
  };

  const removeLine = (index: number) => {
    setDraft((prev) => ({
      ...prev,
      lines:
        prev.lines.length <= 1
          ? prev.lines
          : prev.lines.filter((_, i) => i !== index),
    }));
  };

  // Live estimate over the lines that currently parse; tax is estimated from
  // each line's rate and re-computed by the server from tax_rate_id.
  const taxRateBpById = useMemo(
    () => new Map(taxRates.map((rate) => [rate.id, rate.rate_bp])),
    [taxRates],
  );
  const totals = totalTaxableLines(
    draft.lines.map((line) => ({
      amountCents: toMinor(line.amount) ?? 0,
      rateBp: line.tax_rate_id
        ? taxRateBpById.get(line.tax_rate_id)
        : undefined,
    })),
  );

  // Readable supplier selection from names already used on this organization's
  // bills, with the CRM link carried along when the supplier matches.
  const knownSuppliers = useMemo<KnownSupplier[]>(() => {
    const byName = new Map<string, KnownSupplier>();
    for (const bill of bills ?? []) {
      const name = bill.supplier_name.trim();
      if (!name) continue;
      const key = name.toLowerCase();
      const existing = byName.get(key);
      if (!existing)
        byName.set(key, { name, crmContactId: bill.crm_contact_id });
      else if (!existing.crmContactId && bill.crm_contact_id)
        existing.crmContactId = bill.crm_contact_id;
    }
    return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [bills]);

  const selectSupplier = (name: string) => {
    const match = knownSuppliers.find(
      (supplier) => supplier.name.toLowerCase() === name.trim().toLowerCase(),
    );
    updateDraft({
      partyName: name,
      crmContactId: match?.crmContactId ?? "",
    });
  };

  const statusCounts = (bills ?? []).reduce<Record<string, number>>(
    (counts, bill) => {
      const key = bill.status.toLowerCase();
      counts[key] = (counts[key] ?? 0) + 1;
      return counts;
    },
    {},
  );
  const receivedCount = (bills ?? []).filter((bill) =>
    matchesBillListFilter(bill, "received"),
  ).length;
  const overdueCount = (bills ?? []).filter((bill) =>
    matchesBillListFilter(bill, "overdue"),
  ).length;
  // Bills due within the next week that still carry a balance — the
  // attention strip below the header tallies and filters these.
  const attentionBills = (bills ?? []).filter((bill) =>
    billNeedsAttention(bill),
  );
  const attentionTotalCents = attentionBills.reduce(
    (sum, bill) => sum + billBalanceCents(bill),
    0,
  );

  const visibleBills = useMemo(
    () =>
      (bills ?? [])
        .filter((bill) => !attentionOnly || billNeedsAttention(bill))
        .filter((bill) => matchesBillListFilter(bill, statusTab))
        .filter((bill) => {
          const query = search.trim().toLowerCase();
          if (!query) return true;
          return (
            bill.bill_number.toLowerCase().includes(query) ||
            bill.supplier_name.toLowerCase().includes(query)
          );
        })
        .filter((bill) => {
          const day = bill.due_at.slice(0, 10);
          if (dateFrom && day < dateFrom) return false;
          if (dateTo && day > dateTo) return false;
          return true;
        })
        .sort((a, b) => {
          switch (sort) {
            case "total":
              return b.total_cents - a.total_cents;
            case "number":
              return a.bill_number.localeCompare(b.bill_number);
            case "supplier":
              return a.supplier_name.localeCompare(b.supplier_name);
            default:
              return a.due_at.localeCompare(b.due_at);
          }
        }),
    [bills, attentionOnly, statusTab, search, dateFrom, dateTo, sort],
  );

  const advancedFilterCount =
    (dateFrom ? 1 : 0) + (dateTo ? 1 : 0) + (sort !== "due" ? 1 : 0);

  const clearAdvancedFilters = () => {
    setDateFrom("");
    setDateTo("");
    setSort("due");
  };

  const clearDraft = () => {
    setDraft(newDocumentDraft());
    clearDocumentDraft(BILL_DRAFT_PREFIX, organizationId);
  };

  const payableBills = (bills ?? []).filter(
    (bill) => bill.status === "received" || bill.status === "overdue",
  );
  const payableOptions = payableBills.map((bill) => ({
    value: bill.id,
    label: `${bill.bill_number} — ${bill.supplier_name} (${formatMoney({
      cents: bill.total_cents,
      currency: bill.currency_code || "USD",
    })})`,
    keywords: `${bill.bill_number} ${bill.supplier_name}`,
  }));
  const selectedPayable = payableBills.find((bill) => bill.id === payBillId);
  const payCurrency = selectedPayable?.currency_code || "USD";
  const payEntry = minorUnitEntry(payCurrency);

  const openPay = (billId = "") => {
    setPayBillId(billId);
    setPayAmount("");
    setPayDone(null);
    setError(null);
    setPayOpen(true);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const name = draft.partyName.trim();
      if (!name) throw new Error("Supplier name is required.");
      const dueIso = draft.dueAt ? dateInputToUtcIso(draft.dueAt) : undefined;
      if (draft.dueAt && !dueIso)
        throw new Error("Due date must be a valid date.");
      if (!draft.issueDate || !dateInputToUtcIso(draft.issueDate)) {
        throw new Error("Issue date is required and must be a valid date.");
      }
      if (!draft.dueAt && !draft.crmContactId && !draft.paymentTermId) {
        throw new Error(
          "Choose a supplier record or payment term, or enter a due-date override.",
        );
      }
      if (!draft.dueAt && draft.paymentTermId === "none" && !draft.crmContactId) {
        throw new Error(
          "Enter a due date when no supplier record is selected.",
        );
      }
      await createBill({
        supplier_name: name,
        ...(dueIso ? { due_at: dueIso } : {}),
        issue_date: draft.issueDate,
        ...(draft.paymentTermId
          ? {
              payment_term_id:
                draft.paymentTermId === "none" ? null : draft.paymentTermId,
            }
          : {}),
        ...(draft.crmContactId ? { crm_contact_id: draft.crmContactId } : {}),
        lines: toBillLinePayload(draft.lines),
      });
      clearDraft();
      setCreatorOpen(false);
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setSubmitting(false);
    }
  };

  const submitPay = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPaying(true);
    setError(null);
    setPayDone(null);
    try {
      if (!payBillId) throw new Error("Choose a bill to pay.");
      const amount_minor = toMinorUnits(payAmount, payCurrency);
      if (amount_minor === null)
        throw new Error("Payment amount must be a positive number.");
      const received = new Date(`${payDate}T00:00:00`);
      if (!payDate || Number.isNaN(received.getTime()))
        throw new Error("Payment date is required and must be valid.");
      if (!payMethod.trim()) throw new Error("Payment method is required.");
      const result = await payBill(payBillId, {
        amount_minor,
        method: payMethod.trim(),
        received_at: received.toISOString(),
        ...(payAccountId ? { bank_account_id: payAccountId } : {}),
      });
      setPayDone(
        `Recorded ${formatMoney({ cents: result.amount_cents, currency: payCurrency })} against bill ${result.bill_id}. Bookkeeping only — no bank payment was sent.`,
      );
      setPayAmount("");
      setPayOpen(false);
      reload();
    } catch (cause) {
      if (cause instanceof ApiRequestError && cause.status === 404) {
        setError(`POST /bills/{id}/pay failed: bill not found (404).`);
      } else {
        setError(toErrorMessage(cause));
      }
    } finally {
      setPaying(false);
    }
  };

  const recognize = async (bill: Bill) => {
    if (!bill.issue_date) return;
    setActing(bill.id);
    setError(null);
    try {
      await transitionBill(bill.id, "received");
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Bills"
        description="Supplier bills, recurring plans, and credits."
        actions={
          viewTab === "bills" ? (
            <>
              <Button
                type="button"
                variant="secondary"
                onClick={() => openPay()}
                disabled={payableBills.length === 0}
                title={
                  payableBills.length === 0
                    ? "No received or overdue bills to pay yet"
                    : "Record a payment against a received bill"
                }
              >
                Record payment
              </Button>
              <Button type="button" onClick={() => setCreatorOpen(true)}>
                New bill
              </Button>
            </>
          ) : undefined
        }
      />

      <Tabs
        label="Bill views"
        tabs={[
          { value: "bills", label: "Bills" },
          { value: "recurring", label: "Recurring bills" },
          { value: "credits", label: "Supplier credits" },
        ]}
        value={viewTab}
        onValueChange={(value) =>
          setViewTab(
            value === "recurring"
              ? "recurring"
              : value === "credits"
                ? "credits"
                : "bills",
          )
        }
      >
        {viewTab === "recurring" ? (
          <RecurringBillsPage embedded />
        ) : viewTab === "credits" ? (
          <SupplierCreditsPage embedded />
        ) : (
          <>
      <WorklistSummary
        title="Bill summary"
        testId="bills-summary"
        loading={countsLoading}
        error={countsError}
        onRetry={loadCounts}
        totalCount={counts?.total_count ?? 0}
        openCount={counts?.open_count ?? 0}
        outstandingCents={counts?.outstanding_cents ?? 0}
        overdueCents={counts?.overdue_cents ?? 0}
        aging={counts?.aging ?? []}
        currency={bills?.find((bill) => bill.currency_code)?.currency_code ?? "USD"}
        byStatus={counts?.by_status ?? []}
        activeStatus={statusTab || null}
        onSelectStatus={(status) => {
          setAttentionOnly(false);
          const next = statusTab === status ? "" : status;
          setStatusTab(
            next === "draft" ||
              next === "received" ||
              next === "overdue" ||
              next === "paid" ||
              next === "void"
              ? next
              : "",
          );
        }}
      />

      {attentionBills.length > 0 || attentionOnly ? (
        <button
          type="button"
          aria-pressed={attentionOnly}
          onClick={() => setAttentionOnly((open) => !open)}
          className={`surface flex flex-wrap items-center justify-between gap-3 rounded-2xl border p-4 text-left text-sm ${
            attentionOnly
              ? "border-[var(--danger)]"
              : "border-[var(--border)]"
          }`}
        >
          <span>
            <span className="font-semibold text-[var(--danger)]">
              Due within 7 days
            </span>{" "}
            <span className="text-[var(--muted-foreground)]">
              {attentionOnly
                ? "Showing only these bills."
                : `${attentionBills.length} bill${attentionBills.length === 1 ? "" : "s"} need attention.`}
            </span>
          </span>
          <span className="font-semibold tabular-nums">
            {formatMoney({ cents: attentionTotalCents, currency: "USD" })}
          </span>
        </button>
      ) : null}

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          {error.includes("No Console organization context") ? (
            <p className="mt-1 text-[var(--muted-foreground)]">
              Accounting follows the organization chosen in SYLVIQ Console. Pick one there and relaunch Accounting.
            </p>
          ) : null}
        </div>
      ) : null}

      {payDone ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {payDone}
        </output>
      ) : null}

      <DataTableBlock<Bill>
        rows={visibleBills}
        columns={[
          {
            key: "number",
            header: "Number",
            className: "font-mono text-xs",
            cell: (bill) => (
              <Link
                to="/bills/$billId"
                params={{ billId: bill.id }}
                className="hover:underline"
              >
                {bill.bill_number}
              </Link>
            ),
          },
          {
            key: "supplier",
            header: "Supplier",
            cell: (bill) => bill.supplier_name,
          },
          {
            key: "due",
            header: "Due",
            cell: (bill) => new Date(bill.due_at).toLocaleDateString(),
          },
          {
            key: "status",
            header: "Status",
            cell: (bill) => (
              <Badge tone={statusTone(bill.status)}>{bill.status}</Badge>
            ),
          },
          {
            key: "timing",
            header: "Timing",
            className: "text-sm whitespace-nowrap",
            cell: (bill) => {
              const due = recordDueLabel(bill);
              return (
                <span className="inline-flex items-center gap-2">
                  <Badge tone={due.tone}>{due.label}</Badge>
                  {isPartPaid(bill) ? (
                    <span className="text-xs text-[var(--muted-foreground)]">
                      part paid
                    </span>
                  ) : null}
                </span>
              );
            },
          },
          {
            key: "balance",
            header: "Balance",
            align: "end",
            className: "tabular-nums",
            cell: (bill) =>
              formatMoney({
                cents: billBalanceCents(bill),
                currency: bill.currency_code || "USD",
              }),
          },
          {
            key: "total",
            header: "Total",
            align: "end",
            className: "tabular-nums",
            cell: (bill) =>
              formatMoney({
                cents: bill.total_cents,
                currency: bill.currency_code || "USD",
              }),
          },
          {
            key: "actions",
            header: "Actions",
            align: "end",
            cell: (bill) => (
              <div className="flex justify-end">
                <BillRowActions
                  bill={bill}
                  acting={acting === bill.id}
                  onRecognize={setConfirmRecognize}
                  onPay={openPay}
                />
              </div>
            ),
          },
        ]}
        getRowId={(bill) => bill.id}
        selectedIds={EMPTY_SELECTION}
        onSelectedIdsChange={ignoreSelectionChange}
        selectable={false}
        loading={loading && bills === null}
        filtered={visibleBills.length === 0 && (bills?.length ?? 0) > 0}
        emptyState={
          <p className="text-sm text-[var(--muted-foreground)]">
            No bills yet — create the first one above.
          </p>
        }
        filteredEmptyState={
          <p className="text-sm text-[var(--muted-foreground)]">
            No bills match these filters.
          </p>
        }
        search={
          <label
            htmlFor="bills-search"
            className="grid gap-1 text-xs font-medium"
          >
            Search
            <Input
              id="bills-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Number or supplier…"
              disabled={loading}
            />
          </label>
        }
        filters={
          <label
            htmlFor="bills-status"
            className="grid min-w-40 gap-1 text-xs font-medium"
          >
            Status
            <select
              id="bills-status"
              className="select"
              value={statusTab}
              onChange={(e) =>
                setStatusTab(
                  e.target.value === "draft" ||
                    e.target.value === "received" ||
                    e.target.value === "overdue" ||
                    e.target.value === "paid" ||
                    e.target.value === "void"
                    ? e.target.value
                    : "",
                )
              }
              disabled={loading}
            >
              <option value="">
                All statuses ({(bills ?? []).length})
              </option>
              <option value="draft">Draft ({statusCounts.draft ?? 0})</option>
              <option value="received">
                Awaiting payment ({receivedCount})
              </option>
              <option value="overdue">Overdue ({overdueCount})</option>
              <option value="paid">Paid ({statusCounts.paid ?? 0})</option>
              <option value="void">Void ({statusCounts.void ?? 0})</option>
            </select>
          </label>
        }
        tools={
          <Button
            type="button"
            variant="secondary"
            aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen((open) => !open)}
            disabled={loading}
          >
            Filters
            {advancedFilterCount > 0 ? ` (${advancedFilterCount})` : ""}
          </Button>
        }
        actions={
          advancedFilterCount > 0 ? (
            <Button
              type="button"
              variant="ghost"
              onClick={clearAdvancedFilters}
              disabled={loading}
            >
              Clear
            </Button>
          ) : null
        }
        advancedFilters={
          filtersOpen ? (
            <div className="grid gap-3 sm:grid-cols-3">
              <label
                htmlFor="bills-from"
                className="grid gap-1 text-xs font-medium"
              >
                Due from
                <Input
                  id="bills-from"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  disabled={loading}
                />
              </label>
              <label
                htmlFor="bills-to"
                className="grid gap-1 text-xs font-medium"
              >
                Due to
                <Input
                  id="bills-to"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  disabled={loading}
                />
              </label>
              <label
                htmlFor="bills-sort"
                className="grid gap-1 text-xs font-medium"
              >
                Sort by
                <select
                  id="bills-sort"
                  className="select"
                  value={sort}
                  onChange={(e) => setSort(e.target.value as typeof sort)}
                  disabled={loading}
                >
                  <option value="due">Due date</option>
                  <option value="total">Total</option>
                  <option value="number">Number</option>
                  <option value="supplier">Supplier</option>
                </select>
              </label>
            </div>
          ) : null
        }
        onRefresh={reload}
        refreshing={loading}
        refreshingLabel="Loading…"
        tableLabel="Bills"
        testId="bills"
        mobileClassName="stack-list"
        renderMobileRow={(bill) => {
          const currency = bill.currency_code || "USD";
          const due = recordDueLabel(bill);
          return (
            <article className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4">
              <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
                <Link
                  to="/bills/$billId"
                  params={{ billId: bill.id }}
                  className="record-title underline-offset-4 hover:underline"
                >
                  {bill.bill_number}
                </Link>
                <span className="tabular-nums">
                  {formatMoney({
                    cents: bill.total_cents,
                    currency,
                  })}
                </span>
              </div>
              <CardField label="Supplier">{bill.supplier_name}</CardField>
              <CardField label="Due">
                {new Date(bill.due_at).toLocaleDateString()}
              </CardField>
              <CardField label="Status">
                <Badge
                  tone={statusTone(bill.status)}
                  className="justify-self-start"
                >
                  {bill.status}
                </Badge>
              </CardField>
              <CardField label="Timing">
                <span className="inline-flex items-center gap-2">
                  <Badge tone={due.tone} className="justify-self-start">
                    {due.label}
                  </Badge>
                  {isPartPaid(bill) ? (
                    <span className="text-xs text-[var(--muted-foreground)]">
                      part paid
                    </span>
                  ) : null}
                </span>
              </CardField>
              <CardField label="Still owed">
                <span className="tabular-nums">
                  {formatMoney({
                    cents: billBalanceCents(bill),
                    currency,
                  })}
                </span>
              </CardField>
              <div className="flex flex-wrap justify-end pt-3">
                <BillRowActions
                  bill={bill}
                  acting={acting === bill.id}
                  onRecognize={setConfirmRecognize}
                  onPay={openPay}
                />
              </div>
            </article>
          );
        }}
      />
          </>
        )}
      </Tabs>

      <Drawer
        open={creatorOpen}
        onOpenChange={setCreatorOpen}
        title="New bill"
        description="Record a supplier bill. Closing this panel keeps your draft in this browser session."
        className="w-[min(46rem,100%)]"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setCreatorOpen(false)}
            >
              Back to bills
            </Button>
            {draft.partyName ||
            draft.lines.some((line) => line.description || line.amount) ? (
              <Button type="button" variant="ghost" onClick={clearDraft}>
                Discard draft
              </Button>
            ) : null}
            <Button
              type="submit"
              form="bill-create-form"
              disabled={submitting}
              className="ms-auto"
            >
              {submitting ? "Creating…" : "Create bill"}
            </Button>
          </>
        }
      >
        <form
          id="bill-create-form"
          onSubmit={submit}
          className="grid gap-5"
          aria-label="Create bill"
        >
          <details className="rounded-xl border border-[var(--border)] p-4">
            <summary className="cursor-pointer text-sm font-medium">
              Scan receipts
            </summary>
            <div className="mt-4">
              <ReceiptIntake
                onClose={() => setCreatorOpen(false)}
                onCreated={reload}
              />
            </div>
          </details>
          {error ? (
            <div
              role="alert"
              className="rounded-xl border border-[var(--danger)] p-3 text-sm text-[var(--danger)]"
            >
              {error}
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <label
              htmlFor="bill-supplier"
              className="grid gap-1 text-xs font-medium"
            >
              Supplier
              <Input
                id="bill-supplier"
                list="bill-supplier-options"
                value={draft.partyName}
                onChange={(e) => selectSupplier(e.target.value)}
                required
                placeholder="Search or type a name"
                autoComplete="off"
              />
              <datalist id="bill-supplier-options">
                {knownSuppliers.map((supplier) => (
                  <option key={supplier.name} value={supplier.name} />
                ))}
              </datalist>
              <span className="text-[11px] font-normal text-[var(--muted-foreground)]">
                Pick a past supplier or type a new one.
              </span>
            </label>
            <label
              htmlFor="bill-issue-date"
              className="grid gap-1 text-xs font-medium"
            >
              Issue date
              <Input
                id="bill-issue-date"
                type="date"
                value={draft.issueDate}
                onChange={(e) => updateDraft({ issueDate: e.target.value })}
                required
              />
            </label>
            <label
              htmlFor="bill-payment-term"
              className="grid gap-1 text-xs font-medium"
            >
              Payment term
              <PaymentTermPicker
                id="bill-payment-term"
                value={draft.paymentTermId}
                onChange={(value) => updateDraft({ paymentTermId: value })}
                terms={paymentTerms}
                includeContactDefault
              />
            </label>
            <label
              htmlFor="bill-due"
              className="grid gap-1 text-xs font-medium"
            >
              Due-date override (optional)
              <Input
                id="bill-due"
                type="date"
                value={draft.dueAt}
                onChange={(e) => updateDraft({ dueAt: e.target.value })}
              />
            </label>
          </div>
          {paymentTermsError ? (
            <p className="text-xs text-[var(--danger)]">
              Could not load payment terms: {paymentTermsError}
            </p>
          ) : null}

          <fieldset className="grid gap-3">
            <legend className="text-xs font-medium">Lines</legend>
            {draft.lines.map((line, index) => (
              <DocumentLineRow
                key={line.uid}
                idPrefix={`bill-line-${index}`}
                index={index}
                line={line}
                onChange={(patch) => updateLine(index, patch)}
                onRemove={() => removeLine(index)}
                canRemove={draft.lines.length > 1}
              />
            ))}
            <div>
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  setDraft((prev) => ({
                    ...prev,
                    lines: [...prev.lines, emptyDraftLine()],
                  }))
                }
              >
                Add line
              </Button>
            </div>
          </fieldset>

          <p
            className="text-sm text-[var(--muted-foreground)]"
            aria-live="polite"
            data-testid="bill-totals"
          >
            Subtotal ({draft.lines.length}{" "}
            {draft.lines.length === 1 ? "line" : "lines"}):{" "}
            <span className="font-semibold text-[var(--foreground)]">
              {formatMoney({ cents: totals.subtotalCents, currency: "USD" })}
            </span>{" "}
            — Tax (estimated):{" "}
            <span className="font-semibold text-[var(--foreground)]">
              {formatMoney({ cents: totals.taxCents, currency: "USD" })}
            </span>{" "}
            — Total:{" "}
            <span className="font-semibold text-[var(--foreground)]">
              {formatMoney({ cents: totals.totalCents, currency: "USD" })}
            </span>
          </p>
        </form>
      </Drawer>

      <Drawer
        open={payOpen}
        onOpenChange={setPayOpen}
        title="Record bill payment"
        description="Bookkeeping only — this records the payment you made; it never sends money to a bank."
        className="w-[min(34rem,100%)]"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setPayOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="bill-pay-form"
              disabled={paying || !payBillId}
              className="ms-auto"
            >
              {paying ? "Recording…" : "Record payment"}
            </Button>
          </>
        }
      >
        <form
          id="bill-pay-form"
          onSubmit={submitPay}
          className="grid gap-4"
          aria-label="Record bill payment"
        >
          <label htmlFor="pay-bill" className="grid gap-1 text-xs font-medium">
            Bill to pay
            <SearchSelect
              id="pay-bill"
              value={payBillId}
              onChange={(value) => {
                setPayBillId(value);
                setPayAmount("");
              }}
              options={payableOptions}
              placeholder="Pick a bill…"
              searchPlaceholder="Search number or supplier…"
              emptyMessage="No received or overdue bills"
              disabled={paying}
            />
          </label>
          {selectedPayable ? (
            <p className="text-xs text-[var(--muted-foreground)]">
              Bill total:{" "}
              <span className="font-semibold text-[var(--foreground)] tabular-nums">
                {formatMoney({
                  cents: selectedPayable.total_cents,
                  currency: payCurrency,
                })}
              </span>
            </p>
          ) : null}
          <label
            htmlFor="pay-amount"
            className="grid gap-1 text-xs font-medium"
          >
            Amount ({payCurrency})
            <div className="flex items-center gap-2">
              <Input
                id="pay-amount"
                type="number"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                required
                placeholder={payEntry.placeholder}
                step={payEntry.step}
                min="0"
                inputMode="decimal"
                disabled={paying}
              />
              {selectedPayable ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setPayAmount(
                      (
                        selectedPayable.total_cents /
                        10 ** minorUnitsFor(payCurrency)
                      ).toFixed(minorUnitsFor(payCurrency)),
                    )
                  }
                  disabled={paying}
                >
                  Full
                </Button>
              ) : null}
            </div>
          </label>
          <label
            htmlFor="pay-method"
            className="grid gap-1 text-xs font-medium"
          >
            Method
            <Input
              id="pay-method"
              value={payMethod}
              onChange={(e) => setPayMethod(e.target.value)}
              required
              placeholder="bank_transfer"
              disabled={paying}
            />
          </label>
          <label htmlFor="pay-date" className="grid gap-1 text-xs font-medium">
            Paid on
            <Input
              id="pay-date"
              type="date"
              value={payDate}
              onChange={(e) => setPayDate(e.target.value)}
              required
              disabled={paying}
            />
          </label>
          <label
            htmlFor="pay-account"
            className="grid gap-1 text-xs font-medium"
          >
            Account
            <select
              id="pay-account"
              value={payAccountId}
              onChange={(e) => setPayAccountId(e.target.value)}
              disabled={paying}
              className="h-9 rounded-md border border-[var(--border)] bg-[var(--background)] px-2 text-sm"
            >
              <option value="">Default bank account (1100)</option>
              {bankAccounts
                .filter(
                  (account) =>
                    (account.is_active ?? account.active ?? true) &&
                    account.account_type !== "loan",
                )
                .map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.account_name} (
                    {account.account_type.replaceAll("_", " ")})
                  </option>
                ))}
            </select>
          </label>
        </form>
      </Drawer>

      <Dialog
        open={confirmRecognize !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmRecognize(null);
        }}
        title="Recognize this bill?"
        description={
          confirmRecognize
            ? `Posts the expense and payables journal on ${confirmRecognize.issue_date}, moving ${confirmRecognize.bill_number} from draft to received.`
            : undefined
        }
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirmRecognize(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              const bill = confirmRecognize;
              setConfirmRecognize(null);
              if (bill) void recognize(bill);
            }}
          >
            Recognize bill
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
